import i18next from 'i18next';
import moment from 'moment-timezone';

import {
  GENERIC_MODAL_CANCEL_BUTTON,
  GENERIC_MODAL_CLOSE_BUTTON,
  GENERIC_MODAL_IMPORT_BUTTON,
} from '@commons/Modals/GenericModal/genericModalActions';
import { mapColumnIdsAndNormalizedColumnIds, normalizeJsonObject } from '@backoffice/utils';

import { DATE_DISPLAY_FORMATS } from '@commons/DatePickers/constants';
import { get } from 'lodash';
import { IMPORT_MODAL_STEPS, getValidationFileConfig } from '@commons/Modals/MultiStepModal';
import ImportExcelFileModal from '@backoffice/components/ImportExcelFileModal';
import importTypeXlsConstants from '@commons/Excels/constants/importType.xslx.constant';
import productForecastXlsConstants from '@commons/Excels/constants/productForecast.xlsx.constant';

const COMMON_FILE_CONFIG = {
  width: '542px',
  height: 'auto',
  component: ImportExcelFileModal,
};

export const formatProductForecastBatchUpdateFile = (productForecastWorkbook) => {
  const normalizedProductForecastWorkbook = normalizeJsonObject(productForecastWorkbook);

  const sheetConfiguration = productForecastXlsConstants.getSheetConfiguration();

  const columnIds = productForecastXlsConstants.getNormalizedColumnIds();

  return Object.keys(normalizedProductForecastWorkbook).reduce((acc, sheetName) => {
    if (!acc[importTypeXlsConstants.KEY]) {
      acc[importTypeXlsConstants.KEY] = get(
        normalizedProductForecastWorkbook,
        'ImportType[0].importtype',
        null,
      );
    }

    if (sheetName === sheetConfiguration.PRODUCT_FORECAST.name) {
      acc[sheetConfiguration.PRODUCT_FORECAST.id] = normalizedProductForecastWorkbook[
        sheetName
      ].map((row) => {
        const dateKeys = Object.keys(row).filter((key) =>
          moment(key, DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY, true).isValid(),
        );

        // Create object following pattern { YYYY-MM-DD: value, ... }
        const dateValues = Object.fromEntries(dateKeys.map((key) => [key, row[key]]));

        return {
          ...mapColumnIdsAndNormalizedColumnIds(row, columnIds),
          ...dateValues,
        };
      });
    }

    return acc;
  }, {});
};

export const getImportProductForecastModalConfig = (step, props) => {
  switch (step) {
    case IMPORT_MODAL_STEPS.SELECT_FILE:
      return getSelectFileConfig(props);
    case IMPORT_MODAL_STEPS.VALIDATE_FILE:
      return getValidationFileConfig({
        ...props,
        title: i18next.t('FORECAST.SALES_PLANNING.PRODUCT_FORECAST_TEMPLATE_FILE_IMPORT'),
        subTitle: i18next.t('FORECAST.SALES_PLANNING.PRODUCT_FORECAST_TEMPLATE_FILE_IMPORT'),
      });
    case IMPORT_MODAL_STEPS.VALIDATED_FILE:
      return getValidatedFileConfig(props);
    case IMPORT_MODAL_STEPS.ERROR_FILE:
      return getErrorFileConfig(props);
    default:
      return {
        width: '542px',
        height: 'auto',
        component: ImportExcelFileModal,
      };
  }
};

export const getSelectFileConfig = ({ selectedFile, handleFileChange }) => ({
  ...COMMON_FILE_CONFIG,
  type: 'action',
  title: i18next.t('FORECAST.SALES_PLANNING.PRODUCT_FORECAST_TEMPLATE_FILE_IMPORT'),
  icon: '/images/inpulse/file-upload-black-small.svg',
  data: {
    subTitle: i18next.t('FORECAST.SALES_PLANNING.PRODUCT_FORECAST_TEMPLATE_FILE_IMPORT'),
    selectedFile,
    setSelectedFile: (file) => handleFileChange(file),
  },
  actions: [
    {
      ...GENERIC_MODAL_CANCEL_BUTTON(),
      handleClick: () => handleFileChange(null),
    },
    {
      ...GENERIC_MODAL_IMPORT_BUTTON(),
      isDisabled: true,
      handleClick: () => false,
    },
  ],
});

const getValidatedFileConfig = ({ updatedFile = null, handleFileImport }) => ({
  ...COMMON_FILE_CONFIG,
  type: 'success',
  title: i18next.t('FORECAST.SALES_PLANNING.PRODUCT_FORECAST_TEMPLATE_FILE_IMPORT'),
  icon: '/images/inpulse/check-white-small.svg',
  data: {
    validatedFile: true,
    selectedFile: updatedFile,
  },
  actions: [
    {
      ...GENERIC_MODAL_IMPORT_BUTTON(),
      handleClick: () => handleFileImport(),
    },
  ],
});

export const getErrorFileConfig = ({
  updatedFile,
  handleFileChange,
  errorFile,
  exportErrors,
  statusCode,
  storeName,
}) => {
  const data = {
    subTitle: i18next.t('ADMIN.SUPPLIER_PRODUCTS.MODAL_IMPORT_TEMPLATE_SELECT_FILE_ERROR_SUBTITLE'),
    additionalText: i18next.t(
      'FORECAST.SALES_PLANNING.PRODUCT_FORECAST_TEMPLATE_FILE_IMPORT_ERROR_SUBTITLE',
      {
        storeName,
      },
    ),
    validatedFile: false,
    selectedFile: updatedFile,
    ...(statusCode !== 200 && {
      exportXLS: () => exportErrors(errorFile),
    }),
  };

  return {
    ...COMMON_FILE_CONFIG,
    type: 'error',
    title: i18next.t('ADMIN.SUPPLIER_PRODUCTS.MODAL_IMPORT_TEMPLATE_BATCH_SELECT_FILE_ERROR_TITLE'),
    icon: '/images/inpulse/info-white-small.svg',
    data,
    actions: [
      {
        ...GENERIC_MODAL_CLOSE_BUTTON(),
        handleClick: () => {
          handleFileChange(null);
        },
      },
    ],
  };
};
